import './Account.css';
import { LoginWeb3, ValidateEmail } from './auth';
import { useAccount } from '../hooks';
import { Button, LoadingSymbol, CountrySelect } from './ui';
import { SignupStatuses } from '../enums';
import { useSearchParams } from 'react-router-dom';
import { useMemo } from 'react';
import { useCountryContext } from '../contexts';

type TabType = 'account-info' | 'wallets' | 'email-password';

export function Account() {
   const [searchParams, setSearchParams] = useSearchParams();
   const activeTab = (searchParams.get('tab') || 'account-info') as TabType;
   const { countries } = useCountryContext();

   const {
      user,
      submitEditAccount,
      submitEditPassword,
      userPutForm,
      userPasswordPutForm,
      isUserPasswordLoading,
      isUserLoading,
      allowAccountUpdate,
      setAllowAccountUpdate,
      handleImageDivClick,
      isAvatarLoading,
      handleFileChange,
      fileInputRef,
      submitCheckAddEmail,
      submitAddEmail,
      addEmailStatus,
      isAddEmailLoading,
      addEmailForm,
      setAddEmailStatus,
      resetEmailForm,
      submitResetCheckEmail,
      submitResetEmail,
      resetEmailStatus,
      setResetEmailStatus,
      isResetEmailLoading,

      handleSetPrimaryWallet,
      isUpdatingPrimary,

      handleDeleteWallet,
      isDeletingWallet,
   } = useAccount();

   const handleTabChange = (tab: TabType) => {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('tab', tab);
      setSearchParams(newSearchParams);
   };

   const tabs = useMemo(() => {
      const tabList: { id: TabType; label: string; icon: string }[] = [{ id: 'account-info', label: 'Account Info', icon: '/assets/icons/user-icon.svg' }];

      if (user?.email) {
         tabList.push({ id: 'email-password', label: 'Email & Password', icon: '/assets/icons/email-icon.svg' });
      }

      tabList.push({ id: 'wallets', label: 'Wallets', icon: '/assets/icons/wallet-icon.svg' });

      return tabList;
   }, [user?.email]);

   if (!user) return null;

   return (
      <div className="account-container">
         {/* Tab Navigation */}
         <div className="account-tabs">
            {tabs.map((tab) => (
               <div
                  key={tab.id}
                  className={`account-tab ${activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => handleTabChange(tab.id)}>
                  <img
                     src={tab.icon}
                     alt=""
                     width={20}
                  />
                  <span>{tab.label}</span>
               </div>
            ))}
         </div>

         {/* Tab Content */}
         <div className="account-tab-content flex justify-center">
            {activeTab === 'account-info' && (
               <div className="flex flex-col gap-3 box bg-gray">
                  <div className="flex items-center gap-2">
                     <img
                        src="/assets/icons/user-icon.svg"
                        alt=""
                        width={25}
                     />
                     <h2>Account Info</h2>
                  </div>
                  <div className="flex justify-center">
                     <Button
                        type="transparent"
                        className="pfp-medium-container"
                        onClick={handleImageDivClick}
                        children={
                           <div className="flex flex-col items-center justify-center gap-1">
                              <div
                                 className="pfp-medium"
                                 onKeyDown={() => null}
                                 style={{ backgroundImage: `${isAvatarLoading ? 'unset' : `url(${user.image || '/assets/icons/user-icon.svg'}`}` }}>
                                 {isAvatarLoading && <LoadingSymbol size={20} />}
                              </div>
                              <input
                                 type="file"
                                 accept="image/*"
                                 onChange={handleFileChange}
                                 hidden
                                 ref={fileInputRef}
                              />
                              <span>{!user.image ? 'Upload Image' : 'Change Image'}</span>
                           </div>
                        }
                     />
                  </div>
                  <form
                     onChange={() => setAllowAccountUpdate(true)}
                     onSubmit={userPutForm.handleSubmit(submitEditAccount)}
                     className="account-form flex flex-col gap-3">
                     <div className="flex flex-col gap-2">
                        <div className="flex gap-2 wrap">
                           <div className="form-group flex-1">
                              <label htmlFor="username">Username</label>
                              <input
                                 type="text"
                                 id="username"
                                 {...userPutForm.register('username')}
                                 className={userPutForm.formState.errors.username ? 'error-input' : ''}
                              />
                              {userPutForm.formState.errors.username && <p className="error-message">{userPutForm.formState.errors.username.message as string}</p>}
                           </div>

                           <div className="form-group flex-1">
                              <label htmlFor="fullname">Full Name</label>
                              <input
                                 type="text"
                                 id="fullname"
                                 {...userPutForm.register('fullname')}
                                 className={userPutForm.formState.errors.fullname ? 'error-input' : ''}
                              />
                              {userPutForm.formState.errors.fullname && <p className="error-message">{userPutForm.formState.errors.fullname.message as string}</p>}
                           </div>
                        </div>

                        {user.referralCode && (
                           <div className="form-group">
                              <label htmlFor="referral-code">Referral Code</label>
                              <input
                                 type="text"
                                 id="referral-code"
                                 value={user.referralCode}
                                 disabled
                              />
                           </div>
                        )}

                        <div className="form-group">
                           <label htmlFor="address">Address</label>
                           <input
                              type="text"
                              id="address"
                              {...userPutForm.register('address')}
                              className={userPutForm.formState.errors.address ? 'error-input' : ''}
                           />
                           {userPutForm.formState.errors.address && <p className="error-message">{userPutForm.formState.errors.address.message as string}</p>}
                        </div>

                        <div className="form-group">
                           <label htmlFor="city">City</label>
                           <input
                              type="text"
                              id="city"
                              {...userPutForm.register('city')}
                              className={userPutForm.formState.errors.city ? 'error-input' : ''}
                           />
                           {userPutForm.formState.errors.city && <p className="error-message">{userPutForm.formState.errors.city.message as string}</p>}
                        </div>

                        <div className="flex gap-2 wrap">
                           <div className="form-group flex-1">
                              <label htmlFor="state">State</label>
                              <input
                                 type="text"
                                 id="state"
                                 {...userPutForm.register('state')}
                                 className={userPutForm.formState.errors.state ? 'error-input' : ''}
                              />
                              {userPutForm.formState.errors.state && <p className="error-message">{userPutForm.formState.errors.state.message as string}</p>}
                           </div>

                           <div className="form-group flex-1">
                              <label htmlFor="zip">Zip Code</label>
                              <input
                                 type="text"
                                 id="zip"
                                 {...userPutForm.register('zip')}
                                 className={userPutForm.formState.errors.zip ? 'error-input' : ''}
                              />
                              {userPutForm.formState.errors.zip && <p className="error-message">{userPutForm.formState.errors.zip.message as string}</p>}
                           </div>
                        </div>

                        <div className="form-group">
                           <label htmlFor="country">Country</label>
                           <CountrySelect
                              options={countries}
                              value={countries.find((option) => option.value === userPutForm.watch('country'))}
                              onChange={(selectedCountry: any) => {
                                 userPutForm.setValue('country', selectedCountry?.value ?? '', { shouldValidate: true });
                                 setAllowAccountUpdate(true);
                              }}
                              placeholder="Select your country"
                           />
                           {userPutForm.formState.errors.country && <p className="error-message">{userPutForm.formState.errors.country.message as string}</p>}
                        </div>

                        <div className="form-group">
                           <label htmlFor="phone">Phone</label>
                           <input
                              type="text"
                              id="phone"
                              {...userPutForm.register('phone')}
                              className={userPutForm.formState.errors.phone ? 'error-input' : ''}
                           />
                           {userPutForm.formState.errors.phone && <p className="error-message">{userPutForm.formState.errors.phone.message as string}</p>}
                        </div>
                     </div>

                     <div className="flex flex-col gap-2">
                        <Button
                           title="Save Changes"
                           buttonType="submit"
                           type="white-big"
                           className="submit-button"
                           disabled={!userPutForm.formState.isValid || isUserLoading || !allowAccountUpdate}
                           loading={isUserLoading}
                        />
                     </div>
                  </form>
               </div>
            )}

            {activeTab === 'wallets' && (
               <div className="flex flex-col gap-3 box bg-gray">
                  <div className="flex items-center gap-2">
                     <img
                        src="/assets/icons/wallet-icon.svg"
                        alt=""
                        width={25}
                     />
                     <h2>Wallets</h2>
                  </div>

                  <div className="flex flex-col gap-3">
                     {/* Primary Wallet */}
                     {user.primaryWallet !== null && (
                        <div className="wallet-item primary-wallet">
                           <div className="flex items-center justify-between">
                              <div className="flex justify-between items-center wrap gap-2 flex-1">
                                 <div className="flex items-center gap-2">
                                    <a
                                       className="wallet-name underline"
                                       href={`https://solscan.io/address/${user.primaryWallet.publicKey}`}
                                       target="_blank">
                                       {user.primaryWallet.publicKey.substring(0, 8)}...{user.primaryWallet.publicKey.substring(user.primaryWallet.publicKey.length - 8)}{' '}
                                       {user.primaryWallet.name && `- ${user.primaryWallet.name}`}
                                    </a>
                                    {user.primaryWallet.isSeeker === true && (
                                       <div className="seeker-badge">
                                          <img
                                             src="https://golfn.sfo3.cdn.digitaloceanspaces.com/badges/seeker.png"
                                             alt="Seeker"
                                             className="seeker-badge-img"
                                          />
                                          <span className="seeker-badge-text">SEEKER</span>
                                       </div>
                                    )}
                                 </div>

                                 <div className="flex items-center gap-2">
                                    <span className="primary-badge">Primary</span>
                                    {!user.email && user.wallets.length > 1 && (
                                       <button
                                          className="transparent flex items-center"
                                          onClick={() => handleDeleteWallet(user.primaryWallet?.publicKey || '')}
                                          disabled={isDeletingWallet}>
                                          {isDeletingWallet ? (
                                             <LoadingSymbol size={20} />
                                          ) : (
                                             <img
                                                src="/assets/icons/delete-icon.svg"
                                                alt=""
                                                width={25}
                                             />
                                          )}
                                       </button>
                                    )}
                                 </div>
                              </div>
                           </div>
                        </div>
                     )}

                     {/* Other Wallets */}
                     {user.wallets
                        .filter((wallet) => !wallet.isPrimary)
                        .map((wallet) => (
                           <div
                              key={wallet.publicKey}
                              className="wallet-item flex justify-between items-center gap-2">
                              <div className="flex items-center gap-2">
                                 <a
                                    className="wallet-name underline"
                                    href={`https://solscan.io/address/${wallet.publicKey}`}
                                    target="_blank">
                                    {wallet.publicKey.substring(0, 8)}...{wallet.publicKey.substring(wallet.publicKey.length - 8)} {wallet.name && `- ${wallet.name}`}
                                 </a>
                                 {wallet.isSeeker === true && (
                                    <div className="seeker-badge">
                                       <img
                                          src="https://golfn.sfo3.cdn.digitaloceanspaces.com/badges/seeker.png"
                                          alt="Seeker"
                                          className="seeker-badge-img"
                                       />
                                       <span className="seeker-badge-text">SEEKER</span>
                                    </div>
                                 )}
                              </div>

                              <div className="flex items-center gap-2">
                                 <button
                                    className="transparent underline"
                                    onClick={() => handleSetPrimaryWallet(wallet.publicKey)}
                                    disabled={isUpdatingPrimary}>
                                    {isUpdatingPrimary ? <LoadingSymbol size={20} /> : 'Set as Primary'}
                                 </button>

                                 <button
                                    className="transparent flex items-center"
                                    onClick={() => handleDeleteWallet(wallet.publicKey)}
                                    disabled={isDeletingWallet}>
                                    {isDeletingWallet ? (
                                       <LoadingSymbol size={20} />
                                    ) : (
                                       <img
                                          src="/assets/icons/delete-icon.svg"
                                          alt=""
                                          width={25}
                                       />
                                    )}
                                 </button>
                              </div>
                           </div>
                        ))}

                     <LoginWeb3 title={user.wallets.length === 0 ? 'Link a wallet' : 'Link another wallet'} />
                  </div>
               </div>
            )}

            {activeTab === 'email-password' && (
               <>
                  {user.email ? (
                     <div className="flex flex-col items-start gap-3 box bg-gray">
                        <div className="flex flex-col gap-1">
                           <div className="flex items-center gap-2">
                              <img
                                 src="/assets/icons/email-icon.svg"
                                 alt=""
                                 width={25}
                              />
                              <h2>Email</h2>
                           </div>
                        </div>
                        {resetEmailStatus === SignupStatuses.VerificationEmailSent ? null : (
                           <form
                              onSubmit={resetEmailForm.handleSubmit(submitResetCheckEmail)}
                              className="signup-form flex flex-col gap-3">
                              <div className="flex flex-col gap-2">
                                 <div className="form-group">
                                    <input
                                       disabled
                                       type="email"
                                       id="new-email"
                                       {...resetEmailForm.register('email')}
                                       className={resetEmailForm.formState.errors.email ? 'error-input' : ''}
                                    />
                                    {resetEmailForm.formState.errors.email && <p className="error-message">{resetEmailForm.formState.errors.email.message as string}</p>}
                                 </div>
                              </div>
                           </form>
                        )}
                        <form
                           onSubmit={userPasswordPutForm.handleSubmit(submitEditPassword)}
                           className="signup-form flex flex-col gap-3">
                           <div className="flex flex-col gap-2">
                              {user.hasPassword && (
                                 <div className="form-group">
                                    <label htmlFor="current-password">Current Password</label>
                                    <input
                                       type="password"
                                       id="current-password"
                                       {...userPasswordPutForm.register('currentPassword')}
                                       className={userPasswordPutForm.formState.errors.currentPassword ? 'error-input' : ''}
                                    />
                                    {userPasswordPutForm.formState.errors.currentPassword && (
                                       <p className="error-message">{userPasswordPutForm.formState.errors.currentPassword.message as string}</p>
                                    )}
                                 </div>
                              )}

                              <div className="form-group">
                                 <label htmlFor="new-password">New Password</label>
                                 <input
                                    type="password"
                                    id="new-password"
                                    {...userPasswordPutForm.register('newPassword')}
                                    className={userPasswordPutForm.formState.errors.newPassword ? 'error-input' : ''}
                                 />
                                 {userPasswordPutForm.formState.errors.newPassword && (
                                    <p className="error-message">{userPasswordPutForm.formState.errors.newPassword.message as string}</p>
                                 )}
                              </div>
                              <div className="form-group">
                                 <label htmlFor="new-password-confirmation">Confirm Password</label>
                                 <input
                                    type="password"
                                    id="new-password-confirmation"
                                    {...userPasswordPutForm.register('confirmPassword')}
                                    className={userPasswordPutForm.formState.errors.confirmPassword ? 'error-input' : ''}
                                 />
                                 {userPasswordPutForm.formState.errors.confirmPassword && (
                                    <p className="error-message">{userPasswordPutForm.formState.errors.confirmPassword.message as string}</p>
                                 )}
                              </div>
                           </div>

                           <div className="flex flex-col gap-2">
                              <Button
                                 title="Save Password"
                                 buttonType="submit"
                                 type="white-big"
                                 className="submit-button"
                                 disabled={!userPasswordPutForm.formState.isValid || isUserPasswordLoading}
                                 loading={isUserPasswordLoading}
                              />
                           </div>
                        </form>
                     </div>
                  ) : (
                     <div className="flex flex-col items-start gap-3 box bg-gray">
                        <div className="flex items-center gap-2">
                           <img
                              src="/assets/icons/lock-icon.svg"
                              alt=""
                              width={25}
                           />
                           <h2>Add an Email</h2>
                        </div>
                        {addEmailStatus === SignupStatuses.VerificationEmailSent ? (
                           <ValidateEmail
                              email={addEmailForm.getValues('email')}
                              onSubmit={submitAddEmail}
                              onCancel={() => setAddEmailStatus(SignupStatuses.InformationVerified)}
                              isLoading={isAddEmailLoading}
                              onResend={addEmailForm.handleSubmit(submitCheckAddEmail)}
                           />
                        ) : (
                           <form
                              onSubmit={addEmailForm.handleSubmit(submitCheckAddEmail)}
                              className="signup-form flex flex-col gap-3">
                              <div className="flex flex-col gap-2">
                                 <div className="form-group">
                                    <label htmlFor="add-email">Email</label>
                                    <input
                                       type="email"
                                       id="add-email"
                                       {...addEmailForm.register('email')}
                                       className={addEmailForm.formState.errors.email ? 'error-input' : ''}
                                    />
                                    {addEmailForm.formState.errors.email && <p className="error-message">{addEmailForm.formState.errors.email.message as string}</p>}
                                 </div>

                                 <div className="form-group">
                                    <label htmlFor="add-email-password">Password</label>
                                    <input
                                       type="password"
                                       id="add-email-password"
                                       {...addEmailForm.register('password')}
                                       className={addEmailForm.formState.errors.password ? 'error-input' : ''}
                                    />
                                    {addEmailForm.formState.errors.password && <p className="error-message">{addEmailForm.formState.errors.password.message as string}</p>}
                                 </div>
                                 <div className="form-group">
                                    <label htmlFor="add-email-password-confirmation">Confirm Password</label>
                                    <input
                                       type="password"
                                       id="add-email-password-confirmation"
                                       {...addEmailForm.register('confirmPassword')}
                                       className={addEmailForm.formState.errors.confirmPassword ? 'error-input' : ''}
                                    />
                                    {addEmailForm.formState.errors.confirmPassword && (
                                       <p className="error-message">{addEmailForm.formState.errors.confirmPassword.message as string}</p>
                                    )}
                                 </div>

                                 <Button
                                    title="Verify Email"
                                    buttonType="submit"
                                    type="white-big"
                                    className="submit-button"
                                    disabled={!addEmailForm.formState.isValid}
                                    loading={isAddEmailLoading}
                                 />
                              </div>
                           </form>
                        )}
                     </div>
                  )}
               </>
            )}
         </div>
      </div>
   );
}
